// @cursor:editor
import React, { useCallback, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import type { EditorState } from 'lexical';
import LinkDetectionPlugin from '../plugins/LinkDetectionPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LinkPreviewNode } from '../nodes/LinkPreviewNode';

const theme = {
  contentEditable: 'lexical-content-editable',
};

function LexicalErrorBoundary({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

interface LexicalEditorProps {
  initialValue: string; // This should be a serialized Lexical state
  onChange: (value: string) => void; // This will receive serialized Lexical state
  placeholder?: string;
  isEditable?: boolean;
}

const LexicalEditor: React.FC<LexicalEditorProps> = ({ initialValue, onChange, placeholder, isEditable = true }) => {
  const isInitializing = useRef(true);

  const initialConfig = {
    namespace: 'LiteWriteEditor',
    theme,
    onError: console.error,
    editorState: initialValue, // Lexical will parse this JSON string
    nodes: [LinkPreviewNode],
  };

  const handleChange = useCallback((editorState: EditorState) => {
    if (isInitializing.current) {
      isInitializing.current = false;
      return;
    }
    onChange(JSON.stringify(editorState.toJSON()));
  }, [onChange]);

  return (
    <div className="lexical-editor-container">
      <LexicalComposer initialConfig={initialConfig}>
        <PlainTextPlugin
          contentEditable={<ContentEditable className="lexical-content-editable" contentEditable={isEditable} />}
          placeholder={
            <div className="lexical-placeholder">
              {placeholder || "What's happening?"}
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <OnChangePlugin onChange={handleChange} />
        <AutoFocusPlugin />
        <LinkDetectionPlugin />
      </LexicalComposer>
    </div>
  );
};

export default LexicalEditor;